import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { DetailSkeleton } from '../components/PageSkeletons';
import { PullToRefresh } from '../components/PullToRefresh';
import { TournamentSchedule } from '../components/TournamentSchedule';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Trophy, 
  Calendar, 
  Users, 
  DollarSign,
  MapPin,
  Mail,
  Phone,
  Clock,
  Share2,
  Heart,
  HeartOff,
  ArrowLeft,
  Award,
  Play,
  Layout,
  Star,
  Building,
  ExternalLink,
  Loader2,
  Copy,
  CreditCard,
  ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TournamentDetails = () => {
  const { tournamentId } = useParams();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registeredTeams, setRegisteredTeams] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  const [registering, setRegistering] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');

  useEffect(() => {
    fetchTournament();
    fetchRegisteredTeams();
    if (user) {
      checkFavorite();
      fetchUserTeams();
    }
  }, [tournamentId, user]);

  const fetchTournament = async () => {
    try {
      const response = await axios.get(`${API}/tournaments/${tournamentId}`);
      setTournament(response.data);
    } catch (error) {
      console.error('Failed to fetch tournament:', error);
      toast.error('Tournament not found');
      navigate('/tournaments');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegisteredTeams = async () => {
    try {
      const response = await axios.get(`${API}/tournaments/${tournamentId}/teams`);
      setRegisteredTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch registered teams:', error);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.some(fav => fav.tournament_id === tournamentId));
    } catch (error) {
      console.error('Failed to check favorites:', error);
    }
  };

  const fetchUserTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch user teams:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Please login to save favorites');
      return;
    }
    
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${tournamentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { tournament_id: tournamentId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: tournament.name,
          text: `Check out this tournament: ${tournament.name}`,
          url: url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard!');
  };

  const handleRegisterTeam = async () => {
    if (!user) {
      toast.error('Please login to register a team');
      navigate('/login');
      return;
    }

    if (!selectedTeamId) {
      toast.error('Please select a team');
      return;
    }

    // Check if team is already registered
    if (registeredTeams.some(t => t.id === selectedTeamId)) {
      toast.error('This team is already registered for this tournament');
      return;
    }

    setRegistering(true);
    try {
      // Create payment checkout for tournament registration
      const response = await axios.post(
        `${API}/payments/tournament-registration`,
        {
          tournament_id: tournamentId,
          team_id: selectedTeamId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start registration');
    } finally {
      setRegistering(false);
      setShowRegisterDialog(false);
    }
  };

  const openRegisterDialog = () => {
    if (!user) {
      toast.error('Please login to register a team');
      navigate('/login');
      return;
    }

    if (userTeams.length === 0) {
      toast.error('You need to create a team first');
      navigate('/teams/create');
      return;
    }

    // Filter out teams already registered
    const availableTeams = userTeams.filter(
      t => !registeredTeams.some(rt => rt.id === t.id)
    );

    if (availableTeams.length === 0) {
      toast.error('All your teams are already registered');
      return;
    }

    setSelectedTeamId('');
    setShowRegisterDialog(true);
  };

  const handleCloneTournament = async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'organizer')) {
      toast.error('Only organizers can clone tournaments');
      return;
    }

    setCloning(true);
    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/clone`,
        {
          new_name: `${tournament.name} (Copy)`,
          new_start_date: null,  // Let user set dates later
          new_end_date: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Tournament cloned successfully!');
      navigate(`/tournaments/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone tournament');
    } finally {
      setCloning(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  const getLevelBadgeColor = (level) => {
    switch (level) {
      case 'national': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'state': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'regional': return 'bg-green-500/10 text-green-500 border-green-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DetailSkeleton />
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  const totalCost = tournament.entry_fee + (tournament.team_registration_fee || 10);

  return (
    <PullToRefresh onRefresh={fetchTournament}>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournament-details-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/tournaments')} className="mb-4 gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Tournaments
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline" className={getStatusColor(tournament.status)}>
            {tournament.status.toUpperCase()}
          </Badge>
          <Badge variant="outline" className={getLevelBadgeColor(tournament.level)}>
            {tournament.level?.charAt(0).toUpperCase() + tournament.level?.slice(1) || 'District'}
          </Badge>
          <Badge variant="secondary">
            {tournament.age_category} • {tournament.gender}
          </Badge>
        </div>
        
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black mb-2">{tournament.name}</h1>
            <p className="text-muted-foreground max-w-2xl">{tournament.description}</p>
          </div>
          
          <div className="flex gap-2">
            {user && (user.id === tournament.created_by || user.role === 'admin') && (
              <Link to={`/tournaments/${tournamentId}/director`}>
                <Button variant="outline" size="sm" className="gap-2" data-testid="director-panel-btn">
                  <ShieldCheck className="w-4 h-4" />
                  Director Panel
                </Button>
              </Link>
            )}
            <Button variant="outline" size="icon" onClick={toggleFavorite} data-testid="favorite-btn">
              {isFavorite ? (
                <Heart className="w-5 h-5 fill-red-500 text-red-500" />
              ) : (
                <HeartOff className="w-5 h-5" />
              )}
            </Button>
            <Button variant="outline" size="icon" onClick={handleShare} data-testid="share-btn">
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quick Info Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Calendar className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">
                  {tournament.start_date === tournament.end_date ? 'Date' : 'Dates'}
                </p>
                <p className="font-semibold text-sm">
                  {tournament.start_date === tournament.end_date
                    ? new Date(`${tournament.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : `${new Date(`${tournament.start_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(`${tournament.end_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Teams</p>
                <p className="font-semibold text-sm">{registeredTeams.length}/{tournament.max_teams}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Entry Fee</p>
                <p className="font-semibold text-sm">${totalCost.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Layout className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="text-xs text-muted-foreground">Fields</p>
                <p className="font-semibold text-sm">{tournament.fields?.length || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Prize Pool Card */}
          {tournament.prize_pool_enabled && tournament.prize_pool_total > 0 && (
            <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-yellow-500/5" data-testid="prize-pool-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <Award className="w-5 h-5" />
                  Prize Pool
                </CardTitle>
                <CardDescription>
                  Total: <span className="font-bold text-2xl text-foreground">${tournament.prize_pool_total.toLocaleString()}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div className="text-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <Trophy className="w-8 h-8 mx-auto mb-1 text-amber-500" />
                    <p className="text-xs text-muted-foreground">1st Place</p>
                    <p className="text-xl font-bold text-amber-600 dark:text-amber-400" data-testid="prize-1st">
                      ${tournament.prize_1st?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-gray-500/10 border border-gray-500/20">
                    <Trophy className="w-8 h-8 mx-auto mb-1 text-gray-400" />
                    <p className="text-xs text-muted-foreground">2nd Place</p>
                    <p className="text-xl font-bold text-gray-500" data-testid="prize-2nd">
                      ${tournament.prize_2nd?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-amber-700/10 border border-amber-700/20">
                    <Trophy className="w-8 h-8 mx-auto mb-1 text-amber-700" />
                    <p className="text-xs text-muted-foreground">3rd Place</p>
                    <p className="text-xl font-bold text-amber-700" data-testid="prize-3rd">
                      ${tournament.prize_3rd?.toLocaleString() || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs for Teams and Schedule */}
          <Tabs defaultValue="teams" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="teams">Registered Teams</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
            </TabsList>
            
            <TabsContent value="teams">
              <Card>
                <CardContent className="p-4">
                  {registeredTeams.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {registeredTeams.map((team, index) => (
                        <div 
                          key={team.id} 
                          className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                          data-testid={`registered-team-${index}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {team.logo_url ? (
                              <img src={team.logo_url} alt={team.name} className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <Users className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{team.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Coach: {team.coach_name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No teams registered yet</p>
                      <p className="text-sm text-muted-foreground">Be the first to register!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="schedule">
              <div className="space-y-4">
                <TournamentSchedule tournamentId={tournamentId} />
                <div className="flex justify-center gap-4">
                  <Link to={`/tournaments/${tournamentId}/pools`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Play className="w-4 h-4" />
                      Pool Play
                    </Button>
                  </Link>
                  <Link to={`/tournaments/${tournamentId}/bracket`}>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Layout className="w-4 h-4" />
                      Bracket
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Registration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Register Your Team</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Entry Fee</span>
                  <span>${tournament.entry_fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Registration Fee</span>
                  <span>${(tournament.team_registration_fee || 10).toFixed(2)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>${totalCost.toFixed(2)}</span>
                </div>
              </div>
              
              {tournament.registration_deadline && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Deadline: {formatDate(tournament.registration_deadline)}</span>
                </div>
              )}
              
              {tournament.status === 'upcoming' && registeredTeams.length < tournament.max_teams ? (
                <Button 
                  className="w-full btn-primary gap-2" 
                  onClick={openRegisterDialog}
                  data-testid="register-team-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  Register Team
                </Button>
              ) : tournament.status === 'upcoming' ? (
                <Button disabled className="w-full">
                  Registration Full
                </Button>
              ) : (
                <Button disabled className="w-full">
                  Registration Closed
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Location Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {tournament.venue_name && (
                <div className="flex items-start gap-2">
                  <Building className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <span>{tournament.venue_name}</span>
                </div>
              )}
              {tournament.venue_address && (
                <p className="text-muted-foreground pl-6">{tournament.venue_address}</p>
              )}
              {tournament.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{tournament.location}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Card */}
          {(tournament.contact_email || tournament.contact_phone) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {tournament.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${tournament.contact_email}`} className="text-primary hover:underline">
                      {tournament.contact_email}
                    </a>
                  </div>
                )}
                {tournament.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${tournament.contact_phone}`} className="text-primary hover:underline">
                      {tournament.contact_phone}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`/tournaments/${tournamentId}/bracket`} className="block">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Layout className="w-4 h-4" />
                  View Bracket
                </Button>
              </Link>
              <Link to={`/tournaments/${tournamentId}/pools`} className="block">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Play className="w-4 h-4" />
                  Pool Play
                </Button>
              </Link>
              {(user?.role === 'admin' || user?.role === 'organizer') && (
                <>
                  <Link to={`/tournaments/${tournamentId}/referees`} className="block">
                    <Button variant="outline" className="w-full justify-start gap-2">
                      <Users className="w-4 h-4" />
                      Assign Referees
                    </Button>
                  </Link>
                  <Link to={`/tournaments/${tournamentId}/bracket-builder`} className="block">
                    <Button variant="outline" className="w-full justify-start gap-2">
                      <Layout className="w-4 h-4" />
                      Bracket Builder
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-2"
                    onClick={handleCloneTournament}
                    disabled={cloning}
                    data-testid="clone-tournament-btn"
                  >
                    {cloning ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    Clone Tournament
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Registration Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Register for Tournament</DialogTitle>
            <DialogDescription>
              Select a team to register for {tournament?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Team</label>
              <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                <SelectTrigger data-testid="team-select">
                  <SelectValue placeholder="Choose a team..." />
                </SelectTrigger>
                <SelectContent>
                  {userTeams
                    .filter(t => !registeredTeams.some(rt => rt.id === t.id))
                    .map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.age_category})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Entry Fee</span>
                <span>${tournament?.entry_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Registration Fee</span>
                <span>${(tournament?.team_registration_fee || 10).toFixed(2)}</span>
              </div>
              <hr />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span>${totalCost.toFixed(2)}</span>
              </div>
            </div>

            <Button 
              className="w-full btn-primary gap-2"
              onClick={handleRegisterTeam}
              disabled={registering || !selectedTeamId}
              data-testid="confirm-register-btn"
            >
              {registering ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              {registering ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
};
